import React from 'react';
import {Grid} from "@mui/material";
import {Failure} from "src/components/molecules/failure";
import {Successful} from "src/components/molecules/successful";
import {CustomerNumber} from "src/components/molecules/customerNumber";
import {Zipcode} from "src/components/molecules/zipcode";
import {EnergyTypeElectricity} from "src/components/molecules/energyTypeElectricity";
import {ElectricityMeter} from "src/components/molecules/electricityMeter";
import {ElectricityMeterReadingDate} from "src/components/molecules/electricityMeterReadingDate";
import {EnergyTypeGas} from "src/components/molecules/energyTypeGas";
import {GasMeter} from "src/components/molecules/gasMeter";
import {GasMeterReadingDate} from "src/components/molecules/gasMeterReadingDate";
import axios from "axios";
import {Formik} from "formik";
import {AuthenticationFailure} from "src/components/molecules/authenticationError";
import {ContractSelection} from "src/components/molecules/contractSelection";
import PrimaryButton from "src/components/atoms/primaryButton";
import SecondaryButton from "src/components/atoms/secondaryButton";
import moment from "moment";
import {RequiredFields} from "src/components/molecules/requiredFields";
import {EasyCustomerInfo} from "./easyCustomerInfo";
import {WrongCustomerNumberInfo} from "./wrongCustomerNumberInfo";
import {ContractIMSInfo} from "src/components/molecules/contractIMSInfo";

export type EnergyType = 'Strom' | 'Gas';
export interface PossibleContract {
    verbrauchsart: EnergyType;
    vertragsnummerVerschleiert: string;
    vertragsnummerLetzteZweiZiffern: string;
    istIMS: boolean;
    obisCode: string;
}

export interface IZsForm {
}

export function ZsForm(props: IZsForm) {
    const [isEasyCustomer, setIsEasyCustomer] = React.useState(false);
    const [isWrongCustomerNumber, setIsWrongCustomerNumber] = React.useState(false);

    const [successElectricity, setSuccessElectricity] = React.useState(false);
    const [successGas, setSuccessGas] = React.useState(false);
    const [authenticated, setAuthenticated] = React.useState(false);
    const [authenticationError, setAuthenticationError] = React.useState(null);
    const [possibleContracts, setPossibleContracts] = React.useState<PossibleContract[]>([]);

    const [electricityPossible, setElectricityPossible] = React.useState(false);
    const [gasPossible, setGasPossible] = React.useState(false);

    const [failureElectricity, setFailureElectricity] = React.useState(false);
    const [failureGas, setFailureGas] = React.useState(false);

    const [loadingGas, setLoadingGas] = React.useState(false);
    const [loadingElectricity, setLoadingElectricity] = React.useState(false);

    const [electricityError, setElectricityError] = React.useState(null);
    const [gasError, setGasError] = React.useState(null);

    const [loading, setLoading] = React.useState(false);

    const [energyTypeError, setEnergyTypeError] = React.useState<string>();
    const [disableAutoSelect, setDisableAutoSelect] = React.useState<boolean>(false);

    const isSelectedContractIMS = (
        contractNumber: string | undefined,
        type: EnergyType,
        contracts: PossibleContract[]
    ): boolean => {
        if (!contractNumber) return false;
        const c = contracts.find(v => v.verbrauchsart === type && v.vertragsnummerVerschleiert === contractNumber);
        return !!c?.istIMS;
    };

    const getSelectedContractObisCode = (
        contractNumber: string | undefined,
        type: EnergyType,
        contracts: PossibleContract[]
    ): string => {
        if (!contractNumber) return '';
        const contract = contracts.find(v => v.verbrauchsart === type && v.vertragsnummerVerschleiert === contractNumber);
        return contract?.obisCode ?? '';
    };

    const selectObisCodeFromRegisters = (registers: any): string => {
        if (!Array.isArray(registers) || registers.length === 0) {
            return '';
        }

        const activeRegister = registers.find((register: any) => register?.istAktiv === true);
        return (activeRegister ?? registers[0])?.obisCode ?? '';
    };

    const serializeReadingDate = (value: any): string => {
        return `${moment(value).format('YYYY-MM-DD')}T12:00:00Z`;
    };

    const submitMeterData = (values: any) => {
        setLoading(true);
        if (!values.energyTypeElectricity && !values.energyTypeGas) {
            setEnergyTypeError('Bitte wählen Sie mindestens eine Energieart aus.')
            setLoading(false);
            return;
        } else {
            setEnergyTypeError(undefined);
        }

        const electricityIsIMS = values.energyTypeElectricity && isSelectedContractIMS(values.electricityContractNumber, 'Strom', possibleContracts);
        const electricityObisCode = getSelectedContractObisCode(values.electricityContractNumber, 'Strom', possibleContracts);
        const gasObisCode = getSelectedContractObisCode(values.gasContractNumber, 'Gas', possibleContracts);
        const shouldSubmitElectricity = values.energyTypeElectricity && !electricityIsIMS && !successElectricity;
        const shouldSubmitGas = values.energyTypeGas && !successGas;

        if (shouldSubmitElectricity) {
            setFailureElectricity(false);
        }
        if (shouldSubmitGas) {
            setFailureGas(false);
        }

        if (!shouldSubmitElectricity && !shouldSubmitGas) {
            setLoading(false);
            return;
        }

        // Wenn nur ein Strom-IMS-Vertrag ausgewählt ist, soll kein Zählerstand gesendet werden.
        if ((values.energyTypeElectricity && electricityIsIMS) && !values.energyTypeGas) {
            setLoading(false);
            return;
        }

        if (shouldSubmitElectricity) {
            setLoadingElectricity(true);
            axios.post('/readings', {
                kundennummer: values.customerNumber,
                postleitzahl: values.zc,
                vertragsnummer: values.electricityContractNumber,
                verbrauchsart: 'Strom',
                obisCode: electricityObisCode,
                zaehlerstand: String(~~values.elecMeter),
                ablesedatum: serializeReadingDate(values.electricityMeterReadingDate)
            }).then((response) => {
                setLoadingElectricity(false);
                setSuccessElectricity(true);
            }).catch((error) => {
                setLoadingElectricity(false);
                setFailureElectricity(true)
                setElectricityError(error.response.data.messageLocalized);
            })
        }
        if (shouldSubmitGas) {
            setLoadingGas(true);
            axios.post('/readings', {
                kundennummer: values.customerNumber,
                postleitzahl: values.zc,
                vertragsnummer: values.gasContractNumber,
                verbrauchsart: 'Gas',
                obisCode: gasObisCode,
                zaehlerstand: String(~~values.gasMeter),
                ablesedatum: serializeReadingDate(values.gasMeterReadingDate)
            }).then((response) => {
                setLoadingGas(false);
                setSuccessGas(true);
            }).catch((error) => {
                setLoadingGas(false);
                setFailureGas(true)
                setGasError(error.response.data.messageLocalized);
            })
        }
        setLoading(false);

    }

    const authenticateCustomer = (customerNumber: string, zc: string) => {
        setAuthenticationError(null);
        setIsEasyCustomer(false);
        setIsWrongCustomerNumber(false);

        if (!customerNumber || !zc) {
            return;
        }

        if (customerNumber.length === 8 && !customerNumber.startsWith('2')) {
            if (customerNumber.startsWith('7') || customerNumber.startsWith('1')) {
                setIsEasyCustomer(true);
                return;
            } else {
                setIsWrongCustomerNumber(true);
                return;
            }
        }

        setLoading(true);
        axios.get('/contracts?kundennummer=' + customerNumber + '&postleitzahl=' + zc).then(response => {
            setAuthenticated(true);
            setAuthenticationError(null);

            const vertraege = (response.data?.vertraege ?? []) as any[];
            const mappedContracts: PossibleContract[] = vertraege
                .filter(v => v && (v.verbrauchsart === 'Strom' || v.verbrauchsart === 'Gas'))
                .map(v => {
                    const zaehler = v?.zaehler?.[0]
                    const zaehlerTypAbkuerzung = zaehler?.zaehlerTypAbkuerzung;
                    const obisCode = selectObisCodeFromRegisters(zaehler?.register);
                    const istIMS = v.verbrauchsart === 'Strom' && zaehlerTypAbkuerzung === 'IMS';

                    return {
                        verbrauchsart: v.verbrauchsart,
                        vertragsnummerVerschleiert: v.vertragsnummerVerschleiert,
                        vertragsnummerLetzteZweiZiffern: v.vertragsnummerLetzteZweiZiffern,
                        istIMS: istIMS,
                        obisCode: obisCode,
                    };
                })
                .filter(v => v.vertragsnummerVerschleiert.length > 0);

            setPossibleContracts(mappedContracts);

            setElectricityPossible(mappedContracts.some(v => v.verbrauchsart === 'Strom'));
            setGasPossible(mappedContracts.some(v => v.verbrauchsart === 'Gas'));

            setLoading(false);
        }).catch((error) => {
            setAuthenticated(false);
            setAuthenticationError(error.response.data.messageLocalized);
            setLoading(false);
        })
    }

    const reset = () => {
        setIsEasyCustomer(false);
        setIsWrongCustomerNumber(false);
        setSuccessElectricity(false);
        setSuccessGas(false);
        setFailureElectricity(false);
        setFailureGas(false);
        setLoadingGas(false);
        setLoadingElectricity(false);
        setGasError(null);
        setElectricityError(null);
        setEnergyTypeError(undefined);
        setAuthenticated(false);
        setAuthenticationError(null);
        setPossibleContracts([]);
        setGasPossible(false);
        setElectricityPossible(false);
        setLoading(false);
        setDisableAutoSelect(false);
    }

    return (
        <Formik onSubmit={submitMeterData} onReset={reset} validateOnBlur
                validateOnChange={false} initialValues={{
            electricityMeterReadingDate: null,
            gasMeterReadingDate: null,
            customerNumber: '',
            elecMeter: '',
            gasMeter: '',
            energyTypeElectricity: false,
            energyTypeGas: false,
            zc: '',
            electricityContractNumber: '',
            gasContractNumber: ''
        }}>

            {({
                  values,
                  setErrors,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                  resetForm
                  /* and other goodies */
              }) => {
                return (
                    <>

                        <Grid container direction="row" spacing={4} style={{marginBottom: '50px'}}>
                            <Grid item xs={12} md={12}>
                                <div style={{fontWeight: 700}}>Kontaktdaten</div>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomerNumber disabled={authenticated}
                                                authentication={authenticateCustomer}/>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Zipcode disabled={authenticated} authentication={authenticateCustomer}/>
                            </Grid>

                            {isEasyCustomer && (
                              <EasyCustomerInfo/>
                            )}

                            {isWrongCustomerNumber && (
                              <WrongCustomerNumberInfo/>
                            )}

                            {authenticationError && (
                              <AuthenticationFailure message={authenticationError}/>
                            )}

                            {authenticated && (
                                <>
                                    <Grid item xs={12} md={12}>
                                        <div style={{marginTop: '10px', fontWeight: 700}}>
                                            <span style={{color: '#F00'}}>*</span> Energieart
                                        </div>
                                        {(energyTypeError && !values.energyTypeGas && !values.energyTypeElectricity) && (
                                            <span style={{
                                                display: 'block',
                                                color: '#d32f2f',
                                                fontSize: '0.75rem',
                                                marginLeft: '18px',
                                                fontWeight: '400',
                                                lineHeight: '1.66',
                                                fontFamily: 'Open Sans,Roboto,Helvetica Neue'
                                            }}> {energyTypeError}</span>
                                        )}
                                    </Grid>

                                    <Grid item md={6}>
                                        {electricityPossible && (
                                            <EnergyTypeElectricity gasPossible={gasPossible} disabled={successElectricity}
                                                                   disableAutoselect={disableAutoSelect}/>
                                        )}
                                    </Grid>
                                    <Grid item md={6}>
                                        {gasPossible && (<EnergyTypeGas electricityPossible={electricityPossible}
                                                                        disabled={successGas}
                                                                        disableAutoselect={disableAutoSelect}/>)}
                                    </Grid>

                                    {(failureElectricity || failureGas || successElectricity || successGas) && (
                                        <Grid item xs={12} md={12}>

                                            {failureElectricity && (
                                                <Failure message={electricityError} type="Strom"/>
                                            )}

                                            {failureGas && (
                                                <Failure message={gasError} type="Gas"/>
                                            )}

                                            {successElectricity && (
                                                <Successful type="Strom"/>
                                            )}

                                            {successGas && (
                                                <Successful type="Gas"/>
                                            )}

                                        </Grid>
                                    )}


                                    {(electricityPossible && !successElectricity && values.energyTypeElectricity) && (
                                        <Grid item xs={12} md={6}>
                                                <Grid container direction="row"
                                                      spacing={4}>
                                                    <Grid item xs={12} md={12}>
                                                        <div
                                                            style={{fontWeight: 700}}>Zählerdaten
                                                            Strom
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} md={12}>
                                                        <ContractSelection data={possibleContracts}
                                                                           type={'Strom'}
                                                                           name={'electricityContractNumber'}/>
                                                    </Grid>

                                                    {isSelectedContractIMS(values.electricityContractNumber, 'Strom', possibleContracts) ? (
                                                        <Grid item xs={12} md={12}>
                                                            <ContractIMSInfo/>
                                                        </Grid>
                                                    ) : (
                                                        <>
                                                            <Grid item xs={12} md={12}>
                                                                <ElectricityMeter/>
                                                            </Grid>
                                                            <Grid item xs={12} md={12}>
                                                                <ElectricityMeterReadingDate/>
                                                            </Grid>
                                                        </>
                                                    )}

                                                </Grid>
                                        </Grid>
                                    )}
                                    {(gasPossible && !successGas && values.energyTypeGas) && (
                                        <Grid item xs={12} md={6}>
                                                <Grid container direction="row"
                                                      spacing={4}>
                                                    <Grid item xs={12} md={12}>
                                                        <div
                                                            style={{fontWeight: 700}}>Zählerdaten
                                                            Gas
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={12} md={12}>
                                                        <ContractSelection data={possibleContracts} type={'Gas'}
                                                                           name={'gasContractNumber'}/>
                                                    </Grid>
                                                    <Grid item xs={12} md={12}>
                                                        <GasMeter/>
                                                    </Grid>
                                                    <Grid item xs={12} md={12}>
                                                        <GasMeterReadingDate/>
                                                    </Grid>
                                                </Grid>
                                        </Grid>)}
                                </>
                            )}


                        </Grid>

                        <Grid container direction="row" spacing={3} alignContent={"center"} alignItems={"center"}>

                            <Grid item>
                                <SecondaryButton label="Zurücksetzen" onClick={() => resetForm()}
                                                 loading={false}/>
                            </Grid>
                            <Grid item>
                                {(authenticated) && (
                                    <PrimaryButton label="Senden" loading={loadingGas || loadingElectricity}
                                                   onClick={(event: any) => handleSubmit(event)}/>
                                )}

                                {(!authenticated && !loadingGas && !loadingElectricity) && (
                                    <PrimaryButton label="Prüfen" loading={loading}
                                                   onClick={() => authenticateCustomer(values.customerNumber, values.zc)}/>
                                )}

                            </Grid>
                        </Grid>

                        <RequiredFields/>


                    </>)
            }}
        </Formik>);
}
